/**
 * i18n Configuration
 * 
 * Centralized internationalization setup using react-i18next.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enWizard from '../locales/en/wizard.json';
import enMealplan from '../locales/en/mealplan.json';
import enRecipes from '../locales/en/recipes.json';
import enProfile from '../locales/en/profile.json';
import enPremium from '../locales/en/premium.json';
import enPages from '../locales/en/pages.json';
import enAbout from '../locales/en/about.json';

import esCommon from '../locales/es/common.json';
import esAuth from '../locales/es/auth.json';
import esWizard from '../locales/es/wizard.json';
import esMealplan from '../locales/es/mealplan.json';
import esRecipes from '../locales/es/recipes.json';
import esProfile from '../locales/es/profile.json';
import esPremium from '../locales/es/premium.json';
import esPages from '../locales/es/pages.json';
import esAbout from '../locales/es/about.json';

import ruCommon from '../locales/ru/common.json';
import ruAuth from '../locales/ru/auth.json';
import ruWizard from '../locales/ru/wizard.json';
import ruMealplan from '../locales/ru/mealplan.json';
import ruRecipes from '../locales/ru/recipes.json';
import ruProfile from '../locales/ru/profile.json';
import ruPremium from '../locales/ru/premium.json';
import ruPages from '../locales/ru/pages.json';
import ruAbout from '../locales/ru/about.json';

import caCommon from '../locales/ca/common.json';
import caAuth from '../locales/ca/auth.json';
import caWizard from '../locales/ca/wizard.json';
import caMealplan from '../locales/ca/mealplan.json';
import caRecipes from '../locales/ca/recipes.json';
import caProfile from '../locales/ca/profile.json';
import caPremium from '../locales/ca/premium.json';
import caPages from '../locales/ca/pages.json';
import caAbout from '../locales/ca/about.json';

import frCommon from '../locales/fr/common.json';
import frAuth from '../locales/fr/auth.json';
import frWizard from '../locales/fr/wizard.json';
import frMealplan from '../locales/fr/mealplan.json';
import frRecipes from '../locales/fr/recipes.json';
import frProfile from '../locales/fr/profile.json';
import frPremium from '../locales/fr/premium.json';
import frPages from '../locales/fr/pages.json';
import frAbout from '../locales/fr/about.json';

import roCommon from '../locales/ro/common.json';
import roAuth from '../locales/ro/auth.json';
import roWizard from '../locales/ro/wizard.json';
import roMealplan from '../locales/ro/mealplan.json';
import roRecipes from '../locales/ro/recipes.json';
import roProfile from '../locales/ro/profile.json';
import roPremium from '../locales/ro/premium.json';
import roPages from '../locales/ro/pages.json';
import roAbout from '../locales/ro/about.json';

import itCommon from '../locales/it/common.json';
import itAuth from '../locales/it/auth.json';
import itWizard from '../locales/it/wizard.json';
import itMealplan from '../locales/it/mealplan.json';
import itRecipes from '../locales/it/recipes.json';
import itProfile from '../locales/it/profile.json';
import itPremium from '../locales/it/premium.json';
import itPages from '../locales/it/pages.json';
import itAbout from '../locales/it/about.json';

import ukCommon from '../locales/uk/common.json';
import ukAuth from '../locales/uk/auth.json';
import ukWizard from '../locales/uk/wizard.json';
import ukMealplan from '../locales/uk/mealplan.json';
import ukRecipes from '../locales/uk/recipes.json';
import ukProfile from '../locales/uk/profile.json';
import ukPremium from '../locales/uk/premium.json';
import ukPages from '../locales/uk/pages.json';
import ukAbout from '../locales/uk/about.json';


// Combine all namespaces for each language
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    wizard: enWizard,
    mealplan: enMealplan,
    recipes: enRecipes,
    profile: enProfile,
    premium: enPremium,
    pages: enPages,
    about: enAbout,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    wizard: esWizard,
    mealplan: esMealplan,
    recipes: esRecipes,
    profile: esProfile,
    premium: esPremium,
    pages: esPages,
    about: esAbout,
  },
  ru: {
    common: ruCommon,
    auth: ruAuth,
    wizard: ruWizard,
    mealplan: ruMealplan,
    recipes: ruRecipes,
    profile: ruProfile,
    premium: ruPremium,
    pages: ruPages,
    about: ruAbout,
  },
  ca: {
    common: caCommon,
    auth: caAuth,
    wizard: caWizard,
    mealplan: caMealplan,
    recipes: caRecipes,
    profile: caProfile,
    premium: caPremium,
    pages: caPages,
    about: caAbout,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    wizard: frWizard,
    mealplan: frMealplan,
    recipes: frRecipes,
    profile: frProfile,
    premium: frPremium,
    pages: frPages,
    about: frAbout,
  },
  ro: {
    common: roCommon,
    auth: roAuth,
    wizard: roWizard,
    mealplan: roMealplan,
    recipes: roRecipes,
    profile: roProfile,
    premium: roPremium,
    pages: roPages,
    about: roAbout,
  },
  it: {
    common: itCommon,
    auth: itAuth,
    wizard: itWizard,
    mealplan: itMealplan,
    recipes: itRecipes,
    profile: itProfile,
    premium: itPremium,
    pages: itPages,
    about: itAbout,
  },
  uk: {
    common: ukCommon,
    auth: ukAuth,
    wizard: ukWizard,
    mealplan: ukMealplan,
    recipes: ukRecipes,
    profile: ukProfile,
    premium: ukPremium,
    pages: ukPages,
    about: ukAbout,
  },
};

// Supported languages configuration
export const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

i18n
  // Detect user language from browser/localStorage
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: 'es',
    supportedLngs: ['es', 'en', 'ru', 'ca', 'fr', 'ro', 'it', 'uk'],
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: ['common', 'auth', 'wizard', 'mealplan', 'recipes', 'profile', 'premium', 'pages', 'about'],
    
    // Language detection options
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Key to store selected language in localStorage
      lookupLocalStorage: 'i18nextLng',
      // Cache the detected language
      caches: ['localStorage'],
    },

    interpolation: {
      // React already escapes values
      escapeValue: false,
    },

    // Disable suspense to prevent race conditions
    react: {
      useSuspense: false,
    },

    // Debug mode (disable in production)
    debug: process.env.NODE_ENV === 'development',
  });

/**
 * Change the current language
 * @param {string} languageCode - The language code (e.g., 'en', 'es')
 */
export const changeLanguage = (languageCode) => {
  i18n.changeLanguage(languageCode);
};

/**
 * Get the current language code (normalized to base code, e.g. 'ru' not 'ru-RU')
 * @returns {string} The current language code
 */
export const getCurrentLanguage = () => {
  const lang = i18n.language || 'en';
  // i18n.language can be 'ru-RU', 'en-US', etc. — normalize to base code
  const base = lang.split('-')[0];
  return supportedLanguages.some(l => l.code === base) ? base : 'en';
};

/**
 * Check if a language is supported
 * @param {string} languageCode - The language code to check
 * @returns {boolean} True if the language is supported
 */
export const isLanguageSupported = (languageCode) => {
  return supportedLanguages.some(lang => lang.code === languageCode);
};

export default i18n;
